import clsx from "clsx";
import type { ReactNode } from "react";

/**
 * 관리자 화면 공용 조각. 조회 화면(`history/HistoryPrimitives`)과 톤은 같게 하되,
 * 여기 있는 것들은 전부 "입력/저장" 을 위한 요소라 별도 파일로 둔다.
 */

/** 설정 섹션 카드 — 제목 + 설명 + 우측 액션 슬롯. */
export function AdminSection({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-4 shadow-sm sm:p-5 dark:border-surface-darkBorder dark:bg-surface-darkSubtle">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            {title}
          </h3>
          {description && (
            <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {description}
            </p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export type ButtonTone = "primary" | "default" | "danger";

const TONE_CLASSES: Record<ButtonTone, string> = {
  primary:
    "bg-slate-800 text-white hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200",
  default:
    "border border-surface-border bg-white text-slate-700 hover:bg-slate-50 dark:border-surface-darkBorder dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
  danger:
    "border border-status-majorOutage/40 bg-status-majorOutage-bg text-status-majorOutage-fg hover:bg-status-majorOutage-bg/70 dark:border-status-majorOutage-dark/40 dark:bg-status-majorOutage-darkBg dark:text-status-majorOutage-darkFg",
};

export function AdminButton({
  tone = "default",
  type = "button",
  disabled,
  onClick,
  children,
}: {
  tone?: ButtonTone;
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        // 모바일 44px — 관리자 화면은 저장/삭제처럼 되돌리기 번거로운 동작이 많아
        // 오탭 위험을 줄이는 게 특히 중요하다.
        "inline-flex min-h-[2.75rem] items-center justify-center rounded-md px-3 py-2 text-xs font-semibold transition-colors",
        "sm:min-h-0 sm:py-1.5",
        "disabled:cursor-not-allowed disabled:opacity-50",
        TONE_CLASSES[tone],
      )}
    >
      {children}
    </button>
  );
}

/**
 * 시간(정수) 입력. 비우면 `null` 을 올려보내는 것이 핵심이다 —
 * 주차장 예외에서 "빈 칸 = 전역값 사용" 을 표현한다.
 */
export function HourInput({
  value,
  onChange,
  placeholder,
  label,
  hint,
  min = 1,
  max = 8760,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  /** 비어 있을 때 회색으로 비치는 값 — 보통 전역 기본값. */
  placeholder?: string;
  label: string;
  hint?: string;
  min?: number;
  max?: number;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
        {label}
      </span>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === "") {
            onChange(null);
            return;
          }
          const n = Number.parseInt(raw, 10);
          onChange(Number.isNaN(n) ? null : n);
        }}
        // 모바일: 높이 44px + 16px 글자. iOS Safari 는 16px 미만 입력에 포커스하면
        // 페이지를 자동 확대해버려서, 숫자 하나 고치고 나면 레이아웃이 틀어진 채로 남는다.
        className="w-full min-h-[2.75rem] rounded-md border border-surface-border bg-white px-2 py-1.5 text-base tabular-nums text-slate-800 placeholder:text-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:min-h-0 sm:text-sm dark:border-surface-darkBorder dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-600"
        aria-label={label}
      />
      {hint && (
        <span className="text-[10px] text-slate-400 dark:text-slate-500">{hint}</span>
      )}
    </label>
  );
}

/** 텍스트 입력 한 줄. */
export function TextInput({
  value,
  onChange,
  label,
  placeholder,
  hint,
  type = "text",
  autoComplete,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  placeholder?: string;
  hint?: string;
  type?: "text" | "password";
  autoComplete?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
        {label}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        // HourInput 과 동일 규칙 — 모바일 44px + 16px 글자(iOS 자동 확대 방지).
        className="w-full min-h-[2.75rem] rounded-md border border-surface-border bg-white px-2.5 py-1.5 text-base text-slate-800 placeholder:text-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:min-h-0 sm:text-sm dark:border-surface-darkBorder dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-600"
        aria-label={label}
      />
      {hint && (
        <span className="text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
          {hint}
        </span>
      )}
    </label>
  );
}

/** 체크박스 + 라벨 + 보조 설명. */
export function CheckboxRow({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        // 체크박스는 4x4(16px)로 두되 라벨 전체가 <label> 이라 탭 영역은 이미 넓다.
        // 모바일에서는 체크박스 자체도 조금 키워 단독으로 눌러도 잡히게 한다.
        className="mt-0.5 h-6 w-6 shrink-0 rounded border-surface-border text-slate-800 focus-visible:ring-2 focus-visible:ring-slate-400 sm:h-4 sm:w-4 dark:border-surface-darkBorder"
      />
      <span>
        <span className="block text-xs font-medium text-slate-700 dark:text-slate-200">
          {label}
        </span>
        {hint && (
          <span className="block text-[10px] leading-relaxed text-slate-400 dark:text-slate-500">
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}

/** 저장 결과/오류 알림 줄. */
export function Notice({
  tone,
  children,
}: {
  tone: "success" | "error" | "info";
  children: ReactNode;
}) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={clsx(
        "rounded-md border px-3 py-2 text-xs leading-relaxed",
        tone === "success" &&
          "border-status-operational/30 bg-status-operational-bg text-status-operational-fg dark:border-status-operational-dark/30 dark:bg-status-operational-darkBg dark:text-status-operational-darkFg",
        tone === "error" &&
          "border-status-majorOutage/30 bg-status-majorOutage-bg text-status-majorOutage-fg dark:border-status-majorOutage-dark/30 dark:bg-status-majorOutage-darkBg dark:text-status-majorOutage-darkFg",
        tone === "info" &&
          "border-surface-border bg-slate-50 text-slate-600 dark:border-surface-darkBorder dark:bg-slate-800/50 dark:text-slate-300",
      )}
    >
      {children}
    </div>
  );
}

/** 작은 라벨 칩 — "예외", "감시 제외" 등. */
export function Chip({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "warn" | "muted";
  children: ReactNode;
}) {
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
        tone === "neutral" &&
          "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
        tone === "warn" &&
          "bg-status-degraded-bg text-status-degraded-fg dark:bg-status-degraded-darkBg dark:text-status-degraded-darkFg",
        tone === "muted" &&
          "bg-status-retired-bg text-status-retired-fg dark:bg-status-retired-darkBg dark:text-status-retired-darkFg",
      )}
    >
      {children}
    </span>
  );
}
